import { ButtonBase } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useRecordContext } from "react-admin";
import { useQuery } from "react-query";
import { getStudentPracticalProgress } from "../api/studentPracticalProgress";
import type { BookedTraining } from "../model/BookedTraining";
import type { Student } from "../providers/studentsProvider";
import { useDialog } from "../hooks/useDialog";
import { StudentPracticalProgressDialog } from "../dialogs/StudentPracticalProgressDialog";
import { formatPracticalProgress } from "../utils/practicalProgress";
import { TrainingItem } from "../details/student/cards/TrainingItem";

export function StudentPracticalProgressField(props: { training: BookedTraining }) {
  const { training } = props;
  const student = useRecordContext<Student>();
  return (
    <_ProgressField
      key={`${student.id}:${training.id}:${training.drivingLicenseClass}:${student.practicalExamLocationUid ?? ""}`}
      student={student}
      training={training}
    />
  );
}

function _ProgressField(props: { student: Student; training: BookedTraining }) {
  const { student, training } = props;
  const { dialogProps, openDialog } = useDialog();
  const { data, isError, isFetching, refetch } = useQuery(
    ["studentPracticalProgress", student.id, training, student.practicalExamLocationUid],
    () => getStudentPracticalProgress(student, training),
    { onError: (error) => handleError("Der praktische Fortschritt konnte nicht geladen werden.", error) },
  );
  const percentage = !isError && data ? formatPracticalProgress(data.percentage) : undefined;
  const value = isError ? "Nicht verfügbar" : percentage;

  return (
    <>
      <ButtonBase
        onClick={openDialog}
        disabled={!data && !isError}
        aria-label={
          value ? `Praktischen Fortschritt im Detail anzeigen: ${value}` : "Praktischer Fortschritt wird geladen"
        }
        sx={{
          width: "100%",
          display: "block",
          textAlign: "left",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
          "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
        }}
      >
        <TrainingItem
          icon={<VerifiedIcon />}
          label="Fortschritt"
          value={percentage ? percentage.slice(0, -1) : value}
          unit={percentage ? "%" : undefined}
          loadingLabel="Praktischer Fortschritt wird geladen"
          muted={!!(training.isCancelled || training.isFinished)}
        />
      </ButtonBase>
      {dialogProps.open && (
        <StudentPracticalProgressDialog
          {...dialogProps}
          studentName={student.name}
          drivingLicenseClass={training.drivingLicenseClass}
          data={data}
          isError={isError}
          isFetching={isFetching}
          onRetry={() => {
            void refetch();
          }}
        />
      )}
    </>
  );
}
